import { Alert, Box, Theme } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { withNavBars } from "../../HOCs";
import { Layout } from "../../components";
import { SecondaryNavBars } from "../../interfaces";
import { useAppDispatch, useAppSelector } from "../../redux";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import {
  reset
} from "../../redux/slices/bom";
// import { HasAccess } from "../../router/authorization";
// import { PERMISSIONS } from "../../router/permission";
import { AppRoutes } from "../../router/routes";
import { NavBars, PricingModelOptions } from "./utils";
import { BasicDetail } from "./basicDetail";
import { useLazyGetBOMsQuery, useLazyGetPricingByIdQuery } from "../../redux/services";
import { updatePricingSlice } from "../../redux/slices/pricing";
import { Summary } from "./summary";
import { CostingDetails } from "./costDetails";
import Preview from "./Preview";
import { CostGroup, Costing, CustomCost } from "pdf-generator";
import { entityOptions } from "../viewQuotes/itemDetails/utils";
import { UserAccessManagement } from "../userAccessManagement";
import { PERMISSIONS } from "../../router/permission";
import { HasAccess } from "../../router/authorization";
import { TAG_CANONICAL_NAMES } from "../../utils";

export interface ChildProps { readOnly: boolean, refetchCosting: () => void, refetch?: () => void, costingData: { base_currency: string; costing: Partial<Costing>; hasCosting: boolean; masterData?: (string | number)[][], levelsData?: (string | number)[][] } }

export interface ValidationError {
  sequence_id: string;
  _id: string;
  errors: string[];
}

export const usePricingStyles = (theme: Theme) => ({
  root: {
    flexGrow: 1,
    background: theme.palette.background.paper,
    boxShadow: `rgba(99, 99, 99, 0.2) 0px 2px 8px 0px`,
    height: "100%",
    overflow: "auto",
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
});

export const ViewPricing: React.FC<{
  children?: JSX.Element;
  currentPath: string;
  avoidNavigate?: boolean;
}> = ({
  currentPath,
  avoidNavigate,
  ...props
}) => {

    const { p_id, id = "New" } = useParams();
    const navigate = useNavigate();
    const location: any = useLocation();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { pricing } = useAppSelector((store) => store);
    const { perm } = useAppSelector(store => store.auth.userDetails);

    const [getBOMs, bomRes] = useLazyGetBOMsQuery();

    const [getPricing, pricingRes] = useLazyGetPricingByIdQuery();

    const [hideSideNav, setHideSideNav] = React.useState(false);

    const isEdit = p_id !== ":id" && id !== "New";

    const pricingSubPaths = [
      AppRoutes.viewPricingBasicDetail(id),
      AppRoutes.viewPricingSummary(id),
      AppRoutes.viewPricingPreview(id),
      AppRoutes.manageUsersOnPricing(id),
    ];

    const currentHistory = React.useMemo(() => {
      let additionalPaths = hideSideNav ? [
        {
          label: "Master Pricing",
          onClick: () => navigate(AppRoutes.viewPricingSummary(id)),
        }
      ] : [];
      return [
        {
          label: 'Commercial Module',
          onClick: () => navigate(AppRoutes.pricingList)
        },
        {
          label: `Pricing`,
          onClick: () => navigate(AppRoutes.pricingList)
        },
        {
          label: isEdit ? pricing.sequenceId ?? `...` : "New",
          onClick: () => navigate(AppRoutes.viewPricingBasicDetail(id)),
        },
        ...additionalPaths
      ];
      // eslint-disable-next-line
    }, [id, pricing.sequenceId, hideSideNav])

const [selected, updateSelected] = React.useState<string[]>(["1"]);

const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_PRICING_CREATE), [perm]);
const updatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_PRICING_UPDATE), [perm]);

const isReadOnly = isEdit ? pricing?.released || !updatable : !creatable;

const updateOnTabClick = (nav: SecondaryNavBars) => {
  updateSelected([nav.id]);
};

const giveMeComponent = () => {
  // Basic Details
  if (matchPath(location.pathname, AppRoutes.viewPricingBasicDetail(id)) || (avoidNavigate && selected.includes("1"))) {
    return (<BasicDetail readOnly={isReadOnly} savedPricing={pricingRes?.data} refetch={() => fetchPricing(id)} />);
  }

  // Costing Details
  if (matchPath(location.pathname, AppRoutes.viewPricingCostingDetail(id))) {
    return (<CostingDetails readOnly={isReadOnly} refetch={() => fetchPricing(id)} />);
  }

  // Summary
  if (matchPath(location.pathname, AppRoutes.viewPricingSummary(id)) || (avoidNavigate && selected.includes("2"))) {
    return <Summary readOnly={isReadOnly} refetch={() => fetchPricing(id)} />;
  }

  // Preview
  if (matchPath(location.pathname, AppRoutes.viewPricingPreview(id)) || (avoidNavigate && selected.includes("3"))) {
    return <Preview readOnly={isReadOnly} footerURL={`${window.location.origin}${AppRoutes.viewPricingBasicDetail(id)}`} refetch={() => fetchPricing(id)} isFetched={pricingRes.isSuccess} />;
  }

  // User Management
  if (matchPath(location.pathname, AppRoutes.manageUsersOnPricing(id)) || (avoidNavigate && selected.includes("4"))) {
    return <UserAccessManagement updatable={updatable} isLoading={pricingRes.isFetching || pricingRes.isLoading} object_id={id} object_type="bomPricing" refetch={() => fetchPricing(id)} user_access={pricingRes?.data?.user_access} sequence_id={pricingRes?.data?.sequence_id ?? "..."} created_user={pricingRes?.data?.created_user} />
  }

  return <></>;
};

const fetchPricing = async (id: string) => {
  if (isEdit) {
    await getPricing({ pricing_id: id }).unwrap().catch(err => {
      enqueueSnackbar(
        "Oops! Something went wrong, Unable to fetch the pricing data",
        {
          variant: "error",
        }
      );
      console.error("error: ", err)
    }).finally(() => {
      dispatch(closeBackdrop());
    });
  }
}

React.useEffect(() => {
  setHideSideNav(matchPath(location.pathname, AppRoutes.viewPricingCostingDetail(id)) !== null);
  // eslint-disable-next-line
}, [location.pathname])

React.useEffect(() => {
  if (isEdit) {
    dispatch(openBackdrop("Fetching Pricing Details..."));
    getBOMs({ page: null, pageSize: null, include_histories: true });
    fetchPricing(id);
  } else {
    dispatch(reset());
    dispatch(closeBackdrop());
  }
  // eslint-disable-next-line
}, [p_id, id]);

React.useEffect(() => {
  if (isEdit && pricingRes.isSuccess) {
    const bom = bomRes.data?.data?.find(b => b?._id === pricingRes.data.bom_cost?._id);

    const association = pricingRes.data?.associations?.at(-1);
    const object_detail = association?.object_details?.at(-1);

    dispatch(updatePricingSlice({
      basic_details: {
        pricing_name: pricingRes.data.pricing_name,
        remarks: pricingRes.data.remarks ?? "",
        bom_id: bom ? { ...bom, value: bom._id, label: `${bom.sequence_id}-r${bom.version} (${bom.short_description})` } : { label: pricingRes.data.bom_cost?._id, value: pricingRes.data.bom_cost?._id },
        pricing_model: pricingRes.data.pricing_model ? PricingModelOptions[0] : null,
        object_type: entityOptions.find(e => e.value === association?.object_type) || null,
        object_id: object_detail?._id ? { ...object_detail, value: object_detail?._id, label: `${object_detail?.sequence_id ?? "..."}-r${object_detail?.version ?? 0}` } : null,
        priority: pricingRes.data?.tags_info?.filter((_: any) => _.tag_group_canonical_name === TAG_CANONICAL_NAMES.price_priority)?.map((_: any) => ({ ..._, label: _.tag_name, value: _.tag_id }))?.[0] ?? null,
      },
      id: pricingRes.data?._id,
      sequenceId: pricingRes.data.sequence_id,
      ...pricingRes.data,
      exchange_rates: pricingRes.data?.exchange_rates,
      bom_cost: Object.keys(pricingRes.data?.bom_cost).length > 0 ? pricingRes.data?.bom_cost : {
        _id: pricingRes.data.bom_id,
        child_id: ""
      },
      cost_groups: pricingRes.data?.cost_groups?.map((c: CostGroup, idx: number) => ({
        position: idx + 1,
        title: c.title,
        quantity: Number(c.quantity) || 1,
        discount_percentage: Number(c.discount_percentage) || 0,
        total_amount: Number(c.total_amount) || 0,
        value: c.value ?? 0,
        costs: c.costs?.map((cost, idx: number) => ({
          position: idx + 1,
          description: cost.description,
          units: cost.units,
          value: Number(cost.value) || 0,
          quantity: Number(cost.quantity) || 0,
          total_cost: Number(cost.total_cost) || 0
        })) ?? []
      })) ?? [],
      discounts: pricingRes.data?.discounts?.map((d: CustomCost, idx: number) => ({
        position: idx + 1,
        description: d.description,
        type: d.type,
        value: Number(d.value) || 0,
        subtract: d.subtract
      })) ?? [],
      taxes: pricingRes.data?.taxes?.map((d: CustomCost, idx: number) => ({
        position: idx + 1,
        description: d.description,
        type: d.type,
        value: Number(d.value) || 0,
        subtract: d.subtract
      })) ?? [],
      other_costs: pricingRes.data?.other_costs?.map((d: CustomCost, idx: number) => ({
        position: idx + 1,
        description: d.description,
        type: d.type,
        value: Number(d.value) || 0,
        subtract: d.subtract
      })) ?? [],
      report_config: pricingRes.data?.report_config,
      associations: pricingRes.data?.associations ?? [],
      serial_num: pricingRes.data?.serial_num ?? undefined,
      margin_ratio: pricingRes.data?.margin_ratio || 0,
    }))
  }
  // eslint-disable-next-line
}, [pricingRes.status, bomRes.status])

return (
  (<Box
    sx={{
      p: 0.5,
      height: "100%"
    }}>
    <Layout
      history={currentHistory}
      currentPath={hideSideNav ? `Costing breakup for ${pricing.bom_cost?.sequence_id ?? ""}-r${pricing.bom_cost?.version ?? 0}` : currentPath}
      navBars={NavBars(pricingSubPaths, isEdit)}
      mainPanel={<>
        {/* Alert Box */}
        {false && <Alert sx={{ mb: 1 }} severity="info">{
          "This item is published. Changes, overwrite or deleting is not allowed. Only revision is allowed."
        }</Alert>}
        {giveMeComponent()}
      </>}
      sideNavVariant={"whiteboard"}
      otherBreadscrumProps={{
        hideMenuBtn: false,
      }}
      avoidNavigate={avoidNavigate}
      onClick={updateOnTabClick}
      selected={selected}
      hideSideNavBar={hideSideNav}
      resizable
    />
  </Box>)
);
  };

export default withNavBars(ViewPricing);
